import {
  ControlForecastList,
  ForecastVisualizationControl,
  getIntervalFromWindowSize,
} from '@e2m-customer-portal/domains';
import { Device, MarketLocation, useQueryParams } from '@e2m-customer-portal/hooks';
import { isCoLocation, MachineTechnology, NullableMeasure } from '@e2m-customer-portal/shared';
import { ErrorComponent, Loading, PageTitle } from '@e2m-customer-portal/ui';
import { eachDayOfInterval, getTime, startOfToday } from 'date-fns';
import { ForecastProperty } from 'generated/graphql';
import * as R from 'ramda';
import { ReactElement, Suspense, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

export interface DeviceWithInstalledPower extends Device {
  installedPower: NullableMeasure;
}

export interface ControlEnergyForecastProps {
  marketLocation: MarketLocation;
  currentForecastProperty?: ForecastProperty;
}

export function ControlEnergyForecast({
  marketLocation,
  currentForecastProperty,
}: Readonly<ControlEnergyForecastProps>): ReactElement<ControlEnergyForecastProps> {
  const [t] = useTranslation();

  const queryParams = useQueryParams();
  const allDevices: Array<DeviceWithInstalledPower> = marketLocation.machines
    .filter(
      (machine) =>
        !isCoLocation(marketLocation.technologies) || machine.machineTechnology === MachineTechnology.BATTERY,
    )
    .flatMap((machine) => machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })));

  const selectedIds = JSON.parse(
    localStorage.getItem(`device-selection-${marketLocation.id}`) ??
      JSON.stringify(queryParams.has('selected') ? queryParams.getAll('selected') : R.pluck('id', allDevices)),
  ) as Array<string>;
  const [shownDevices, setShownDevices] = useState(
    allDevices.length > 1 && allDevices.length !== selectedIds.length
      ? allDevices.filter((device) => selectedIds.includes(device.id))
      : allDevices,
  );

  useEffect(() => {
    if (allDevices.length > 1 && allDevices.length !== shownDevices.length)
      localStorage.setItem(
        `device-selection-${marketLocation.id}`,
        JSON.stringify(shownDevices.map((device) => device.id)),
      );
    else localStorage.removeItem(`device-selection-${marketLocation.id}`);
  }, [marketLocation.id, shownDevices]);

  const [isEditing, setIsEditing] = useState(false);

  const [interval, setInterval] = useState(getIntervalFromWindowSize(startOfToday(), true));

  const handleDateChange = (date: Date): void => {
    setInterval(() => getIntervalFromWindowSize(date));
  };

  const determineInterval = useCallback(() => {
    setInterval(getIntervalFromWindowSize(interval.start));
  }, [interval.start]);

  useEffect(() => {
    window.addEventListener('resize', determineInterval);
    return () => window.removeEventListener('resize', determineInterval);
  }, [interval, determineInterval]);

  return (
    <>
      <div className="bg-blue-grey-200 items-center justify-between sm:flex">
        <PageTitle label={t('MASTER_DATA_TAB_PLANT')} title={marketLocation.externalName ?? marketLocation.name} />
      </div>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <ForecastVisualizationControl
          marketLocation={marketLocation}
          availableDevices={allDevices}
          devices={shownDevices}
          onDevicesChange={(newDevices: Array<Device>) => {
            if (newDevices.length) setShownDevices(newDevices as Array<DeviceWithInstalledPower>);
          }}
          showDevices={!!allDevices.length}
          date={interval.start}
          days={eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end)).length}
          onDateChange={handleDateChange}
          isEditing={isEditing}
        />
        <Suspense fallback={<Loading />}>
          <ControlForecastList
            devices={allDevices}
            shownDevices={shownDevices}
            interval={interval}
            setIsEditing={setIsEditing}
            currentForecastProperty={currentForecastProperty}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
